import React, { createContext, useContext, useState, ReactNode } from 'react';

export type Language = 'ru' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  getStringResourceTable: () => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

/**
 * Provider component for language context
 * Manages the current language selection and provides helper methods
 */
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('ru'); // Default to Russian
  
  /**
   * Get the appropriate StringResource table name based on selected language
   */
  const getStringResourceTable = (): string => {
    return language === 'ru' ? 'StringResource_ru' : 'StringResource';
  };
  
  return (
    <LanguageContext.Provider value={{ language, setLanguage, getStringResourceTable }}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Hook for consuming the language context
 * Returns the current language state and controls
 */
export const useLanguageContext = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  
  if (context === undefined) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  
  return context;
};

export default LanguageProvider; 